// HelpAssistant/useBuilderRailInset.js
//
// gordon-glitch-help-button-covers-capture-rail-item-1024w
// fix-help-fab-still-covers-a-rail-item-after-5236
// fix-help-fab-covers-account-menu-1024w-short
//
// The Segment Builder's right-hand tool rail (SideMenu.jsx) and this FAB's
// default bottom-right corner both live in the same screen corner. At tablet-
// landscape heights (measured 1024x780, 1024x768 — see fabPosition.js's
// railObstructionWidth for the live rects) the rail's items sit low enough
// that the FAB's corner rect lands on top of one of them.
//
// #5236 only measured the rail's LAST item (Capture) and clamped the FAB just
// above it. MEASURED live on web-dev.iyuga.net/builder/5512, uid 12675,
// bundle main.01c6289a.js (#5236 deployed), 2026-09-07: that clamp reliably
// clears Capture, but every rail item is ~80-90px tall while the FAB (56px)
// plus its clearance gap (12px) only needs 68px — so landing 68px above one
// item's top boundary always lands INSIDE the item stacked above it:
//   1024x780  FAB (post-#5236) 944,563 - 1000,619   Assistant item 962.5625,551.171875 - 1024,631.171875  -> 2096.5 px^2
// #5242 widened the measured obstruction to the UNION of every rail item's
// rect. That cleared every item — by running the FAB off the top of the
// viewport and onto the app header instead, because the union spans almost
// the whole viewport height and the obstruction was applied VERTICALLY.
// MEASURED live (bundle main.5419c4a9.js, 2026-09-07, 1024x780 and 1024x768):
// FAB 944,8 - 2912px^2 on the header, 2492.33px^2 (87%) on the Account menu,
// elementFromPoint at the account button's centre returning the FAB's svg.
//
// The union rect was right; applying it as a HEIGHT was wrong. The rail is a
// RIGHT-docked, full-height column, so it is cleared SIDEWAYS: this hook now
// reports the rail's INSET WIDTH (viewportWidth - railLeft) and the FAB stays
// bottom-anchored, shifted left of the rail. See fabPosition.js's
// railObstructionWidth for the full arithmetic and both measured viewports.
//
// Same shape as useConsentObstruction.js otherwise: the rail's span is a
// real, measurable rect (not a boolean like the segment-panel case, because a
// rail item does NOT reserve the full viewport width the way a bottom-docked
// banner does — see fabPosition.js's railObstructionWidth for why the
// intersection is checked before an inset is derived from it).

import { useEffect, useState } from "react";
import { railObstructionWidth } from "./fabPosition";

/** The rail's own root, added by SideMenu.jsx (segmentBuilder/Main/WheelLayout). */
export const RAIL_SELECTOR = '[data-testid="segment-builder-side-menu"]';

/**
 * The rail's full vertical span: the union of every rendered item's rect,
 * whatever flag-gated set is currently rendered. NOT just the last item — see
 * the file header for why measuring only the last item still let the FAB land
 * on the item above it.
 */
export const measureRailItemRect = (doc) => {
  const root = doc || (typeof document !== "undefined" ? document : null);
  if (!root || typeof root.querySelector !== "function") return null;
  const rail = root.querySelector(RAIL_SELECTOR);
  if (!rail) return null;
  const items = Array.from(rail.children).filter(
    (el) => typeof el.getBoundingClientRect === "function"
  );
  if (!items.length) return null;
  const rects = items.map((el) => el.getBoundingClientRect());
  return {
    left: Math.min(...rects.map((r) => r.left)),
    top: Math.min(...rects.map((r) => r.top)),
    right: Math.max(...rects.map((r) => r.right)),
    bottom: Math.max(...rects.map((r) => r.bottom)),
  };
};

/**
 * Pure(ish) — exported so a unit test can assert the SAME arithmetic the hook
 * runs, against an injected document/viewport instead of a live browser.
 */
export const computeBuilderRailInsetWidth = ({
  doc,
  viewportWidth,
  viewportHeight,
  fabSize,
} = {}) => {
  const railItemRect = measureRailItemRect(doc);
  const vw = viewportWidth ?? (typeof window !== "undefined" ? window.innerWidth : 1440);
  const vh = viewportHeight ?? (typeof window !== "undefined" ? window.innerHeight : 900);
  return railObstructionWidth({ railItemRect, viewportWidth: vw, viewportHeight: vh, fabSize });
};

/**
 * Live width (px, 0 when inert): how much RIGHT-hand space to reserve so the
 * FAB clamps clear of the builder rail's ENTIRE item stack while staying
 * bottom-anchored. 0 on every non-builder route (the rail is unmounted ->
 * measureRailItemRect returns null) and 0 at any viewport already clear of the
 * stack (see railObstructionWidth's own gating).
 */
export default function useBuilderRailInset() {
  const [width, setWidth] = useState(() => computeBuilderRailInsetWidth());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const measure = () =>
      setWidth((prev) => {
        const next = computeBuilderRailInsetWidth();
        return next === prev ? prev : next;
      });
    // Measure now, and once more after paint: the rail mounts/reflows on
    // route change, so the first synchronous read can miss it.
    measure();
    const raf =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(measure)
        : null;
    window.addEventListener("resize", measure);
    // fix-from-drive-verify-fe-5257: WheelLayout's root container animates
    // `width` over motion.duration.slow (see WheelLayout/styles.js) on a real
    // viewport-width change, so the rail (a right-docked child) slides to its
    // new position instead of snapping. `resize` fires at the START of that
    // transition, not the end — a direct viewport jump (e.g. load at 1280x720,
    // then straight to 1024x768, MEASURED live web-dev.iyuga.net bundle
    // main.8be6fb1f.js: 867.5px^2 stable FAB/rail overlap) gets measured
    // mid-transition and reads the rail's OLD rect. The MutationObserver below
    // only catches added/removed nodes, not a pure CSS transition, so nothing
    // else re-measures once it settles. `transitionend` bubbles to `document`
    // for any element's finished transition, so listening here catches the
    // rail settling without hardcoding the transition's duration.
    document.addEventListener("transitionend", measure);
    const observer =
      typeof MutationObserver !== "undefined" ? new MutationObserver(measure) : null;
    if (observer && document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      if (raf && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(raf);
      }
      window.removeEventListener("resize", measure);
      document.removeEventListener("transitionend", measure);
      if (observer) observer.disconnect();
    };
  }, []);

  return width;
}
